import React, { useState, useEffect, useRef } from 'react';
import { collections } from '../../data/products';
import ProductCard from '../ProductCard/ProductCard';
import ProductPage from '../ProductPage/ProductPage';
import './Collections.css';

const Collections = () => {
  const [activeCollection, setActiveCollection] = useState('diningsets');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const sectionRef = useRef(null);
  const tabsRef = useRef(null);

  const collectionTabs = [
    { key: 'diningsets', label: 'Dining Sets' },
    { key: 'servingsets', label: 'Serving Sets' },
    { key: 'ornamentals', label: 'Ornamentals' },
    { key: 'olivewood', label: 'Olive Wood' },
    { key: 'others', label: 'Others' }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.collection-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [activeCollection]);

  // Listen for requests (e.g. from the footer) to open a specific collection tab
  useEffect(() => {
    const handleOpenCollection = (event) => {
      const key = event.detail;
      if (collectionTabs.some((tab) => tab.key === key)) {
        setActiveCollection(key);
      }
      const section = document.getElementById('collections');
      if (section) {
        const headerHeight = 120;
        const top =
          section.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };

    window.addEventListener('openCollection', handleOpenCollection);
    return () => window.removeEventListener('openCollection', handleOpenCollection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show a scroll hint when the tabs overflow horizontally (mobile)
  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;

    const updateHint = () => {
      const canScroll = tabs.scrollWidth > tabs.clientWidth;
      const atEnd = tabs.scrollLeft + tabs.clientWidth >= tabs.scrollWidth - 4;
      setShowScrollHint(canScroll && !atEnd);
    };

    updateHint();
    tabs.addEventListener('scroll', updateHint, { passive: true });
    window.addEventListener('resize', updateHint);

    return () => {
      tabs.removeEventListener('scroll', updateHint);
      window.removeEventListener('resize', updateHint);
    };
  }, []);

  const handleProductClick = (productId) => {
    setSelectedProduct(productId);
  };

  const handleCloseProduct = () => {
    setSelectedProduct(null);
  };

  return (
    <section id="collections" className="collections section-padding" ref={sectionRef}>
      <div className="container">
        <div className="section-header text-center">
          <h2 className="fade-in">Our Collections</h2>
          <p className="section-subtitle fade-in">
            Explore our curated collections, each telling a story<br />
            of tradition, craftsmanship, and timeless elegance
          </p>
        </div>

        <div className={`collection-tabs-wrapper ${showScrollHint ? 'has-more' : ''}`}>
          <div className="collection-tabs" ref={tabsRef}>
            {collectionTabs.map((tab) => (
              <button
                key={tab.key}
                className={`tab-button ${activeCollection === tab.key ? 'active' : ''}`}
                onClick={() => setActiveCollection(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <span className="tabs-scroll-hint" aria-hidden="true">›</span>
        </div>

        <div className="collection-content">
          <div 
            className="collection-grid"
            data-count={collections[activeCollection]?.length || 0}
          >
            {collections[activeCollection]?.map((product, index) => (
              <div 
                key={product.id} 
                className="collection-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <ProductCard 
                  product={product} 
                  onProductClick={handleProductClick}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedProduct && (
        <ProductPage 
          productId={selectedProduct} 
          onClose={handleCloseProduct}
        />
      )}
    </section>
  );
};

export default Collections;